import type { PublicConfig } from "../lib/api";
import type { Market, User } from "../types";
import { normalizeCommunityPost, type CommunityPost } from "./community";

export const portfolioServices = [
  { id: "portraits", label: "Ritratti" }, { id: "events", label: "Eventi" },
  { id: "products", label: "Fotografia di prodotto" }, { id: "real_estate", label: "Immobili" },
  { id: "weddings", label: "Matrimoni" }, { id: "photo_tours", label: "Tour fotografici" },
  { id: "postproduction", label: "Post-produzione" },
] as const;
export type PortfolioService = typeof portfolioServices[number]["id"];
export type PortfolioInput = { published: boolean; city: string; country: Market; bio: string; equipment: string[]; services: PortfolioService[]; featuredPhotoIds: string[] };
export type PortfolioProfile = {
  userId: string; displayName: string; avatarUrl: string | null; city: string; country: Market; bio: string;
  equipment: string[]; services: PortfolioService[]; published: boolean; followerCount: number;
  following: boolean; isOwner: boolean; featuredPhotos: CommunityPost[]; createdAt: string; updatedAt: string;
};
export type PortfolioPage = { available: boolean; items: PortfolioProfile[]; total: number; page: number; pageSize: number; pagesTotal: number };
export type PortfolioDetail = { available: true; profile: PortfolioProfile; photos: CommunityPost[]; hasMorePhotos: boolean };
export type OwnPortfolio = { available: boolean; profile: PortfolioProfile | null; ownPhotos: CommunityPost[] };

const record = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
const identifier = (value: unknown): value is string => typeof value === "string" && /^[a-f0-9]{32}$/.test(value);
const integer = (value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= min && value <= max;
const market = (value: unknown): value is Market => typeof value === "string" && ["IT", "FR", "ES", "CH"].includes(value);
const plain = (value: unknown, min: number, max: number, multiline = false): value is string => typeof value === "string" && [...value].length >= min && [...value].length <= max
  && ![...value].some((character) => { const code = character.charCodeAt(0); return (code < 32 && !(multiline && [9, 10, 13].includes(code))) || code === 127; });
const timestamp = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
const invalid = (): never => { throw new Error("Il portfolio non ha restituito dati verificabili. Aggiorna la pagina."); };
const service = (value: unknown): value is PortfolioService => portfolioServices.some((option) => option.id === value);
const unique = <T>(values: T[]) => new Set(values).size === values.length;

/** Only actual Gruvo WebP uploads; no external placeholder/remote tracking images. */
export function portfolioMedia(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 500 || /[\s\\]/.test(value)) return null;
  if (/^\/media\/[a-f0-9]{32}\.webp$/.test(value)) return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && ["gruvo.io", "www.gruvo.io"].includes(url.hostname)
      && !url.username && !url.password && !url.port && !url.search && !url.hash
      && /^\/media\/[a-f0-9]{32}\.webp$/.test(url.pathname) ? url.toString() : null;
  } catch { return null; }
}

export function portfolioAvailable(config: PublicConfig | null): boolean {
  return config?.configured === true && (config.portfolio === true || record(config.capabilities)?.portfolio === true);
}

function equipment(value: unknown): string[] {
  if (!Array.isArray(value) || value.length > 10 || !value.every((item) => plain(item, 1, 50) && item.trim() !== "") || !unique(value)) return invalid();
  return [...value];
}
function services(value: unknown): PortfolioService[] {
  if (!Array.isArray(value) || value.length > portfolioServices.length || !value.every(service) || !unique(value)) return invalid();
  return [...value];
}

function photo(value: unknown, authorId?: string): CommunityPost {
  const raw = record(value); const author = record(raw?.author); const image = portfolioMedia(raw?.imageUrl);
  if (!raw || !author || !image || !identifier(author.id) || (authorId !== undefined && author.id !== authorId)
    || !market(author.country) || !plain(author.displayName, 1, 100) || !plain(raw.title, 0, 140)
    || !plain(raw.caption, 0, 4000, true) || !plain(raw.gearCamera, 0, 120) || !plain(raw.gearLens, 0, 120)
    || !timestamp(raw.createdAt)) return invalid();
  const avatar = author.avatarUrl === null ? null : portfolioMedia(author.avatarUrl);
  if (author.avatarUrl !== null && avatar === null) return invalid();
  return normalizeCommunityPost({ ...raw, imageUrl: image, author: { ...author, avatarUrl: avatar } });
}
function photos(value: unknown, max: number, authorId?: string): CommunityPost[] {
  if (!Array.isArray(value) || value.length > max) return invalid();
  const items = value.map((item) => photo(item, authorId));
  if (!unique(items.map((item) => item.id))) return invalid();
  return items;
}

/** Explicit public projection drops any unforeseen email/KYC/payment fields. */
export function normalizePortfolioProfile(value: unknown): PortfolioProfile {
  const row = record(value);
  if (!row || !identifier(row.userId) || !plain(row.displayName, 1, 100) || !plain(row.city, 1, 100)
    || !market(row.country) || !plain(row.bio, 0, 800, true) || typeof row.published !== "boolean"
    || !integer(row.followerCount) || typeof row.following !== "boolean" || typeof row.isOwner !== "boolean"
    || !timestamp(row.createdAt) || !timestamp(row.updatedAt)) return invalid();
  const avatarUrl = row.avatarUrl === null ? null : portfolioMedia(row.avatarUrl);
  if (row.avatarUrl !== null && avatarUrl === null) return invalid();
  const featuredPhotos = photos(row.featuredPhotos, 12, row.userId);
  if (row.published && featuredPhotos.length === 0) return invalid();
  return { userId: row.userId, displayName: row.displayName, avatarUrl, city: row.city, country: row.country, bio: row.bio,
    equipment: equipment(row.equipment), services: services(row.services), published: row.published,
    followerCount: row.followerCount, following: row.following, isOwner: row.isOwner, featuredPhotos,
    createdAt: row.createdAt, updatedAt: row.updatedAt };
}

export function normalizePortfolioPage(value: unknown, requestedPage?: number): PortfolioPage {
  const row = record(value);
  if (!row || typeof row.available !== "boolean" || !Array.isArray(row.items) || !integer(row.total)
    || !integer(row.page, 1, 1000) || row.pageSize !== 24 || !integer(row.pagesTotal)
    || row.pagesTotal !== Math.ceil(row.total / 24) || (requestedPage !== undefined && row.page !== requestedPage)
    || row.items.length > Math.min(24, Math.max(0, row.total - (row.page - 1) * 24))
    || (!row.available && (row.items.length !== 0 || row.total !== 0))) return invalid();
  const items = row.items.map(normalizePortfolioProfile);
  if (items.some((item) => !item.published) || !unique(items.map((item) => item.userId))) return invalid();
  return { available: row.available, items, total: row.total, page: row.page, pageSize: 24, pagesTotal: row.pagesTotal };
}

export function normalizePortfolioDetail(value: unknown, expectedUserId?: string): PortfolioDetail {
  const row = record(value);
  if (!row || row.available !== true || typeof row.hasMorePhotos !== "boolean") return invalid();
  const profile = normalizePortfolioProfile(row.profile);
  if (!profile.published || (expectedUserId !== undefined && profile.userId !== expectedUserId)) return invalid();
  return { available: true, profile, photos: photos(row.photos, 100, profile.userId), hasMorePhotos: row.hasMorePhotos };
}

export function normalizeOwnPortfolio(value: unknown, expectedUserId?: string): OwnPortfolio {
  const row = record(value);
  if (!row || typeof row.available !== "boolean" || !(row.profile === null || record(row.profile))) return invalid();
  const profile = row.profile === null ? null : normalizePortfolioProfile(row.profile);
  if (profile && (!profile.isOwner || (expectedUserId !== undefined && profile.userId !== expectedUserId))) return invalid();
  const ownPhotos = photos(row.ownPhotos, 100, expectedUserId ?? profile?.userId);
  if (ownPhotos.some((item) => !item.isOwner) || (!row.available && (profile !== null || ownPhotos.length !== 0))) return invalid();
  return { available: row.available, profile, ownPhotos };
}

export function portfolioActions(profile: PortfolioProfile, user: User | null, config: PublicConfig | null) {
  const ready = portfolioAvailable(config) && user?.emailVerified === true && identifier(user.id) && identifier(profile.userId);
  const own = user?.id === profile.userId;
  return { follow: ready && profile.published && !own && !profile.isOwner,
    contact: ready && profile.published && !own && !profile.isOwner,
    edit: ready && own && profile.isOwner };
}

/** Canonical mutation payload; no contact fields or frontend-chosen public media. */
export function portfolioInput(value: unknown): PortfolioInput {
  const row = record(value);
  if (!row || Object.keys(row).some((key) => !["published", "city", "country", "bio", "equipment", "services", "featuredPhotoIds"].includes(key))
    || typeof row.published !== "boolean" || !plain(row.city, 1, 100) || !row.city.trim() || !market(row.country)
    || !plain(row.bio, 0, 800, true) || !Array.isArray(row.featuredPhotoIds) || row.featuredPhotoIds.length > 12
    || !row.featuredPhotoIds.every(identifier) || !unique(row.featuredPhotoIds)
    || (row.published && row.featuredPhotoIds.length === 0)) {
    throw new Error("Controlla il profilo e scegli almeno uno dei tuoi scatti pubblicati per renderlo visibile.");
  }
  const gear = equipment(row.equipment).map((item) => item.trim());
  if (!unique(gear.map((item) => item.toLocaleLowerCase("it")))) throw new Error("Indica ogni attrezzatura una sola volta.");
  return { published: row.published, city: row.city.trim(), country: row.country, bio: row.bio.trim(),
    equipment: gear, services: services(row.services), featuredPhotoIds: [...row.featuredPhotoIds] };
}

export function normalizePortfolioFollow(value: unknown): { following: boolean; followerCount: number } {
  const row = record(value);
  if (!row || typeof row.following !== "boolean" || !integer(row.followerCount)) return invalid();
  return { following: row.following, followerCount: row.followerCount };
}

export function normalizePortfolioContact(value: unknown): { conversation: { id: string } } {
  const row = record(value); const conversation = record(row?.conversation);
  if (!conversation || !identifier(conversation.id)) return invalid();
  return { conversation: { id: conversation.id } };
}
